// File: src/components/MatchFlow/FoundMatchPage.jsx

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMatchFlow } from './useMatchFlow';
import { PiHandshake } from 'react-icons/pi';
import EndMatchBottomSheet from './EndMatchBottomSheet';
import EventProgressNavBar from './EventProgressNavBar';
import { FaSpinner } from 'react-icons/fa';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { enforceSpeedmatchState, bindRoutePulseEnforcer } from '../../utils/speedmatchEnforcer';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

function authHeaders() {
  const token =
    localStorage.getItem("accessToken") ||
    JSON.parse(localStorage.getItem("tribe_auth") || "{}")?.token || "";
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

/***************************************************************
 * Helpers
 ***************************************************************/
function computeAge(dateOfBirth) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

function getProfilePicUrl(userObj) {
  return userObj?.profilePicUrl || '/default-avatar.png';
}

/***************************************************************
 * FoundMatchPage
 ***************************************************************/
export default function FoundMatchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const incomingMatchId = location.state?.matchId || null;

  // 1) Context FIRST — we need currentMatch before computing eventId
  const {
    socket,
    match: currentMatch,
    setMatch,
    setIsFinding,
    respondMatch,
    endSession
  } = useMatchFlow();

  // Per-match "seen" flag so the overlay won't replay after reload
  const SEEN_KEY = currentMatch?._id ? `sm_found_seen:${currentMatch._id}` : null;
  const [overlaySeen, setOverlaySeen] = useState(() =>
    SEEN_KEY ? sessionStorage.getItem(SEEN_KEY) === '1' : false
  );
  // 🔁 Resync seen flag if match hydrates after mount
  useEffect(() => {
    if (!currentMatch?._id) return;
    const key = `sm_found_seen:${currentMatch._id}`;
    if (sessionStorage.getItem(key) === '1') setOverlaySeen(true);
  }, [currentMatch?._id]);

  // --- Reload helpers (persist last match id to rehydrate on hard refresh) ---
  const LAST_MATCH_KEY = 'sm:lastFoundMatchId';
  const lastMatchIdRef = useRef(sessionStorage.getItem(LAST_MATCH_KEY) || null);
  useEffect(() => {
    if (currentMatch?._id) {
      try { sessionStorage.setItem(LAST_MATCH_KEY, String(currentMatch._id)); } catch {}
    }
  }, [currentMatch?._id]);

  // 2) Then derive api/eventId (before any effect that uses them)
  const api = process.env.REACT_APP_API_URL;
  const eventId =
    currentMatch?.event?._id ||
    localStorage.getItem('mobileEventId') ||
    null;

  // 3) Now it's safe to run effects that use api/eventId/currentMatch
  const enforcerCheckedRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [rehydrateTried, setRehydrateTried] = useState(false);

  // identities
  const userId    = localStorage.getItem('userId')    || null;
  const sessionId = localStorage.getItem('sessionId') || null;

  // Overlay state
  const [showOverlay, setShowOverlay] = useState(false);
  const [hasDeclined, setHasDeclined] = useState(false);
  const [showEndSheet, setShowEndSheet] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [closing, setClosing] = useState(false);

  // Big header timer (HH:MM:SS)
  const [tparts, setTparts] = useState({ h: '--', m: '--', s: '--' });
  const hasHours = tparts.h !== '00' && tparts.h !== '--';

  const MATCH_COPY_COUNT = 11; // overlay copies
  const OVERLAY_MS = 2100;     // hard gate window for overlay unfurl

  // ---------- EARLY accept-map normalization so hooks can depend on it ----------
  const rawAcceptMap = currentMatch?.acceptMap || {};
  const getAcceptEarly = (id) => {
    const v = typeof rawAcceptMap.get === 'function'
      ? rawAcceptMap.get(String(id))
      : rawAcceptMap[String(id)];
    return v === true || v === 'accepted' || (v && v.status === 'accepted');
  };
  const myStatus = getAcceptEarly(userId);

  // 🔒 Gate-on-accept safety: must be declared BEFORE any early returns
  const gatedRef = useRef(false);
  useEffect(() => {
    if (!currentMatch?._id || overlaySeen || gatedRef.current) return;
    if (!myStatus) return; // only once *I* am accepted
    const eid = currentMatch.event?._id || localStorage.getItem('mobileEventId');
    if (!eid) return;
    try {
      const now = Date.now();
      const until = now + OVERLAY_MS + 150;
      sessionStorage.setItem(`speedmatch_intro_gate:${eid}`, String(until));
      sessionStorage.setItem(`speedmatch_intro_hold:${eid}`, String(now + OVERLAY_MS));
      gatedRef.current = true;
    } catch {}
  }, [myStatus, currentMatch?._id, overlaySeen, OVERLAY_MS]);

  // ✅ If no eventId on hard refresh, don't block UI (prevents white screen)
  useEffect(() => {
    if (!eventId) {
      enforcerCheckedRef.current = true; // treat as checked so redirect effect can run
      setReady(true);
    }
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;
    let alive = true;
    (async () => {
      await enforceSpeedmatchState({ api, eventId, pageKind: 'partner', navigate });
      enforcerCheckedRef.current = true;
      if (alive) setReady(true);
    })();
    return () => { alive = false; };
  }, [api, eventId, navigate]);

  // ✅ Re-enforce on tab focus
  useEffect(() => {
    if (!eventId) return;
    const onFocus = () => {
      enforceSpeedmatchState({ api, eventId, pageKind: 'partner', navigate });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [api, eventId, navigate]);

  useEffect(() => {
    if (!eventId) return;
    const onPush = () =>
      enforceSpeedmatchState({ api, eventId, pageKind: 'partner', navigate });
    window.addEventListener('sm:match-updated', onPush);
    return () => window.removeEventListener('sm:match-updated', onPush);
  }, [api, eventId, navigate]);

  // ✅ Rehydrate match from matchId (location.state OR persisted last match) if we don't have one yet
  useEffect(() => {
    const seedId = incomingMatchId || lastMatchIdRef.current;
    if (currentMatch?._id) { setRehydrateTried(true); return; }
    if (!seedId) { setRehydrateTried(true); return; }

    // If this match was just declined on this tab, do NOT rehydrate it
    try {
      if (sessionStorage.getItem(`sm:declineExit:${seedId}`) === '1') {
        setRehydrateTried(true);
        return;
      }
    } catch {}

    (async () => {
      try {
        const { data } = await axios.get(`${api}/matches/${seedId}`, authHeaders());
        if (data?.match) {
          setMatch(data.match);
          setReady(true);
        }
      } catch {
        // ignore; we'll fall back to redirect
      } finally {
        setRehydrateTried(true);
      }
    })();
  }, [api, currentMatch?._id, incomingMatchId, setMatch]);

  /* 1) If no match => /matchflow (guarded to avoid portal flash and allow rehydrate first) */
  useEffect(() => {
    if (!enforcerCheckedRef.current || !rehydrateTried) return;
    // let overlay-driven nav win
    if (!currentMatch || !currentMatch._id) {
      // If we don't even know the event, don't hang the page — go to portal
      if (!eventId) {
        navigate('/matchflow', { replace: true });
        return;
      }

      if (!closing) {
        setClosing(true);
        if (eventId) {
          enforceSpeedmatchState({ api, eventId, pageKind: 'partner', navigate });
        }
      }
    }
  }, [currentMatch, closing, navigate, showOverlay, rehydrateTried, api, eventId]);

  /* 2) If partner declines => back */
  useEffect(() => {
    if (
      currentMatch?.status === 'declined' &&
      String(currentMatch.declinerId) !== String(userId)
    ) {
      const eId = currentMatch.event?._id || localStorage.getItem('mobileEventId');
      navigate(
        eId
          ? `/matchflow?eventId=${eId}&declined=partner`
          : '/matchflow?declined=partner'
      );
    }
  }, [currentMatch, userId, navigate]);

  useEffect(() => {
    const endsAt = currentMatch?.expiresAt;
    if (!endsAt) {
      setTparts({ h: '--', m: '--', s: '--' });
      return;
    }
    const target = new Date(endsAt);

    const tick = () => {
      const diff = target.getTime() - Date.now();
      if (diff <= 0) {
        setTparts({ h: '00', m: '00', s: '00' });
        return;
      }
      const total = Math.floor(diff / 1000);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      setTparts({
        h: String(h).padStart(2, '0'),
        m: String(m).padStart(2, '0'),
        s: String(s).padStart(2, '0'),
      });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [currentMatch?.expiresAt]);

  /* ---------------- Overlay gating: set the GATE as soon as everyone accepts ---------------- */
  useEffect(() => {
    if (!currentMatch || overlaySeen) return;

    // Don’t gate if this exact match was declined on this tab
    try {
      if (sessionStorage.getItem(`sm:declineExit:${currentMatch._id}`) === '1') return;
    } catch {}

    // Compute "everyone accepted"
    const participants = currentMatch.participants || [];
    const allAccepted =
      participants.length > 0 && participants.every((p) => getAcceptEarly(p._id));
    if (!allAccepted) return;

    const eid = currentMatch.event?._id || localStorage.getItem('mobileEventId');
    if (!eid) return;

    // If not already gated, gate now for the full overlay duration (plus tiny buffer)
    try {
      const key = `speedmatch_intro_gate:${eid}`;
      const until = Number(sessionStorage.getItem(key) || 0);
      const now = Date.now();
      if (!until || until < now) {
        sessionStorage.setItem(key, String(now + OVERLAY_MS + 150));
      }
    } catch {}
  }, [currentMatch, overlaySeen, OVERLAY_MS]);

  /* 4) Everyone accepted => overlay once then navigate (HARD GATE via gate+hold) */
  useEffect(() => {
    if (!currentMatch || overlaySeen) return;

    // don’t replay if this exact match was declined on this tab
    try {
      if (sessionStorage.getItem(`sm:declineExit:${currentMatch._id}`) === '1') return;
    } catch {}

    const path = (window.location.pathname || '').toLowerCase();
    if (
      path.includes('/navigatespotpage') ||
      path.includes('/code-verification') ||
      path.includes('/inmatch')
    ) return;

    // everyone accepted?
    const participants = currentMatch.participants || [];
    const allAccepted =
      participants.length > 0 && participants.every((p) => getAcceptEarly(p._id));
    if (!allAccepted) return;

    const eid = currentMatch.event?._id || localStorage.getItem('mobileEventId');
    if (eid) {
      // Set HOLD for the overlay time (enforcer respects max(gate, hold))
      try {
        sessionStorage.setItem(
          `speedmatch_intro_hold:${eid}`,
          String(Date.now() + OVERLAY_MS)
        );
      } catch {}
    }

    setShowOverlay(true);
    setOverlaySeen(true);
    if (SEEN_KEY) sessionStorage.setItem(SEEN_KEY, '1');

    // after the overlay time, release keys and ask enforcer to move us to Navigate
    if (eid) {
      const release = () => {
        try {
          sessionStorage.removeItem(`speedmatch_intro_hold:${eid}`);
          sessionStorage.removeItem(`speedmatch_intro_gate:${eid}`);
        } catch {}
        enforceSpeedmatchState({ api, eventId: eid, pageKind: 'partner', navigate });
      };
      const t = setTimeout(release, OVERLAY_MS + 60);
      return () => clearTimeout(t);
    }
  }, [api, currentMatch, navigate, overlaySeen, SEEN_KEY, OVERLAY_MS]);

  /* Overlay sound effect (kept) */
  useEffect(() => {
    if (showOverlay) {
      const audio = new Audio('/ambient-flute-notification-3-185275.mp3');
      audio.play().catch(() => {});
    }
  }, [showOverlay]);

  /* 5) Completed => ask server (no client navigate) */
  useEffect(() => {
    if (currentMatch?.status === 'completed') {
      if (eventId) enforceSpeedmatchState({ api, eventId, pageKind: 'partner', navigate });
    }
  }, [currentMatch?.status, api, eventId, navigate]);

  /* 6) Partner ended => ask server (no client navigate) */
  useEffect(() => {
    if (!socket) return;

    const handleMatchEnded = ({ matchId, endedBy }) => {
      if (!currentMatch || currentMatch._id !== matchId) return;
      if (String(endedBy) === String(userId)) return; // ignore self here; your own handler can run elsewhere
      setClosing(true);
      // Clear local to avoid stale UI while server state propagates
      setMatch(null);
      setIsFinding(false);
      if (eventId) enforceSpeedmatchState({ api, eventId, pageKind: 'partner', navigate });
    };

    socket.on('match_ended', handleMatchEnded);
    return () => socket.off('match_ended', handleMatchEnded);
  }, [socket, currentMatch?._id, userId, api, eventId, navigate, setMatch, setIsFinding]);

  /* 7) Expired => ask server (no client navigate) */
  useEffect(() => {
    if (!socket) return;

    const handleMatchExpired = ({ matchId }) => {
      if (!currentMatch || currentMatch._id !== matchId) return;
      setClosing(true);
      setMatch(null);
      setIsFinding(false);
      if (eventId) enforceSpeedmatchState({ api, eventId, pageKind: 'partner', navigate });
    };

    socket.on('match_expired', handleMatchExpired);
    return () => socket.off('match_expired', handleMatchExpired);
  }, [socket, currentMatch?._id, api, eventId, navigate, setMatch, setIsFinding]);

  /* Actions */
  function handleAccept() {
    if (!currentMatch?._id || !userId) return;
    console.log('[Accept] click', { myStatus, userId, matchId: currentMatch._id });

    // 🔒 Optimistic gate/hold the moment *I* accept, so enforcer won't promote
    try {
      const eid = currentMatch.event?._id || localStorage.getItem('mobileEventId');
      if (eid) {
        const now = Date.now();
        const until = now + OVERLAY_MS + 150; // tiny buffer
        sessionStorage.setItem(`speedmatch_intro_gate:${eid}`, String(until));
        sessionStorage.setItem(`speedmatch_intro_hold:${eid}`, String(now + OVERLAY_MS));
      }
    } catch {}

    respondMatch(currentMatch._id, userId, 'accepted');
    // Ask server where we should be; no client nav
    setTimeout(() => {
      if (eventId) enforceSpeedmatchState({ api, eventId, pageKind: 'partner', navigate });
    }, 250);
  }

  function handleDecline() {
    if (!currentMatch?._id || !userId) return;
    setHasDeclined(true);

    // 1) Briefly lock routing & mark this match as a deliberate decline exit
    try {
      const eid = currentMatch.event?._id || localStorage.getItem('mobileEventId');
      if (eid) sessionStorage.setItem(`speedmatch_route_lock:${eid}`, String(Date.now() + 1200));
      sessionStorage.setItem(`sm:declineExit:${currentMatch._id}`, '1');
      // Kill any local seeds/tokens that could rehydrate or replay UI
      sessionStorage.removeItem('sm:lastFoundMatchId');
      sessionStorage.removeItem(`sm:intro:${currentMatch._id}`);
      sessionStorage.removeItem(`sm_found_seen:${currentMatch._id}`);
    } catch {}

    // 2) Clear local match first to prevent any partner UI flash
    setMatch(null);
    setIsFinding(false);

    // 3) Tell server, then let the enforcer route us (server = source of truth)
    respondMatch(currentMatch._id, userId, 'declined');

    setClosing(true);
    if (eventId) {
      enforceSpeedmatchState({ api, eventId, pageKind: 'partner', navigate });
    }
  }

  function handleEndMatch() {
    if (!currentMatch?._id || !userId) return;

    // Prefer server/enforcer routing over client navigation
    endSession(currentMatch._id, userId, sessionId);

    setClosing(true);
    // Optional: clear local match to avoid stale UI while server updates propagate
    setMatch(null);
    setIsFinding(false);

    if (eventId) {
      enforceSpeedmatchState({ api, eventId, pageKind: 'partner', navigate });
    }
  }

  if (!ready) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      </div>
    );
  }

  /* Fallbacks */
  if (!currentMatch || !currentMatch._id) {
    return <div style={{ minHeight:'100vh' }} />;
  }

  const participants = currentMatch.participants || [];
  const meIndex = participants.findIndex((p) => String(p._id) === String(userId));
  const others = participants.filter((p, i) => i !== meIndex);

  if (!others.length) {
    return (
      <div style={styles.noMatchContainer}>
        <h2 style={styles.fallbackTitle}>No partner info. Returning...</h2>
        <button style={styles.fallbackBtn} onClick={() => navigate('/matchflow')}>
          Back to MatchFlow
        </button>
      </div>
    );
  }

  // Choose the visible partner BEFORE using `partner` anywhere below
  let partnerIndex = 0;
  if (others.length === 2) partnerIndex = activeIndex;
  const partner = others[partnerIndex] || others[0];

  // partner status uses the same accept getter
  const partnerStatus = getAcceptEarly(partner?._id);

  const highlightedNameSpans = others.map((p, idx) => {
    const fn = p.firstName || 'Someone';
    const ln = p.lastName ? p.lastName.charAt(0).toUpperCase() + '.' : '';
    const fullName = (fn + ' ' + ln).trim();
    return (
      <React.Fragment key={p._id || idx}>
        {idx > 0 && (
          <span style={{ color: '#6b7280', fontWeight: 600, margin: '0 6px' }}>
            &amp;
          </span>
        )}
        <span style={styles.nameHighlight}>{fullName}</span>
      </React.Fragment>
    );
  });

  const picUrl = getProfilePicUrl(partner);
  const age = computeAge(partner.dateOfBirth);
  const gender = partner.gender || '';

  function handleNext() { setActiveIndex((prev) => (prev === 0 ? 1 : 0)); }
  function handlePrev() { setActiveIndex((prev) => (prev === 0 ? 1 : 0)); }

  const step = currentMatch?.flowStep || 1;
  const totalSteps = 5;

  const pageCount = others.length; // 1 (two-person) or 2 (three-person)
  const canPrev = pageCount > 1 && activeIndex > 0;
  const canNext = pageCount > 1 && activeIndex < pageCount - 1;

  return (
    <>
      {/* Overlay when both accept */}
      {showOverlay && (
        <div style={styles.matchOverlay}>
          <div style={styles.overlayContent}>
            <div className="itsA">IT&apos;S A</div>
            <div className="matchMain">
              <div className="matchFilled">MATCH</div>
              <div className="hollowWrapper">
                {Array.from({ length: MATCH_COPY_COUNT })
                  .map((_, i) => i)
                  .reverse()
                  .map((i) => (
                    <div
                      key={i}
                      className="matchHollow"
                      style={{
                        animationDelay: `${(MATCH_COPY_COUNT - 1 - i) * 0.1667}s`,
                        zIndex: -(MATCH_COPY_COUNT - i),
                      }}
                    >
                      MATCH
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Overlay-only styles */}
          <style>
            {`
              @keyframes fadeSlide {
                from { opacity: 0; transform: translateY(-15px); }
                to   { opacity: 1; transform: translateY(0); }
              }
              .itsA {
                font-size: 4rem;
                font-weight: 700;
                color: #3498db;
                animation: fadeSlide 0.0667s ease-in-out forwards;
                margin-bottom: -40px;
                font-family: "Arial Black", sans-serif;
                letter-spacing: .02em;
              }
              .matchMain { text-align: center; }
              .matchFilled {
                font-size: 6.5rem;
                font-weight: 900;
                color: #3498db;
                animation: fadeSlide 0.0333s ease-in-out forwards;
                margin-bottom: 0;
                font-family: "Arial Black", sans-serif;
                letter-spacing: .01em;
              }
              .hollowWrapper {
                display: flex;
                flex-direction: column;
                align-items: center;
                pointer-events: none;
              }
              .matchHollow {
                position: relative;
                font-size: 6.5rem;
                font-weight: 900;
                color: #fff;
                -webkit-text-stroke: 2px rgba(52,152,219,0.3);
                text-stroke: 2px rgba(52,152,219,0.3);
                font-family: "Arial Black", sans-serif;
                opacity: 0;
                animation: fadeSlide 0.0333s ease-in-out forwards;
                margin-top: -80px;
                clip-path: inset(35% 0 0 0);
                letter-spacing: .01em;
              }
              @media (max-width: 412px) {
                .matchFilled, .matchHollow { font-size: 6rem; }
              }
            `}
          </style>
        </div>
      )}

      {/* Top nav */}
      <EventProgressNavBar
        title="MatchFlow"
        leftLabel="End"
        onLeftClick={() => setShowEndSheet(true)}
        currentStep={step}
        totalSteps={totalSteps}
      />

      {/* End bottom sheet */}
      <EndMatchBottomSheet
        visible={showEndSheet}
        onCancel={() => setShowEndSheet(false)}
        onConfirm={() => {
          setShowEndSheet(false);
          handleEndMatch();
        }}
      />

      <div style={styles.pageContainer}>
        {/* Mixer-style crooked highlight headline + subheading + big timer */}
        <div style={styles.headlineWrap}>
          <h2 style={styles.title}>
            Match{' '}
            <span style={styles.highlight}>
              <span style={styles.highlightText}>Invitation</span>
            </span>
          </h2>

          <div style={styles.subheading}>
            We would like to connect you with {highlightedNameSpans}
          </div>

          {/* Big timer */}
          <div style={styles.timerWrapper}>
            <div style={styles.timerLabelPill}>Timer Ends In</div>
            <div style={styles.digitsRow} aria-live="polite">
              {hasHours && (
                <>
                  <span style={styles.digit}>{tparts.h[0]}</span>
                  <span style={styles.digit}>{tparts.h[1]}</span>
                  <span style={styles.colon}>:</span>
                </>
              )}
              <span style={styles.digit}>{tparts.m[0]}</span>
              <span style={styles.digit}>{tparts.m[1]}</span>
              <span style={styles.colon}>:</span>
              <span style={styles.digit}>{tparts.s[0]}</span>
              <span style={styles.digit}>{tparts.s[1]}</span>
            </div>
          </div>
        </div>

        {/* Partner card (stacked) */}
        {(() => {
          const hasTrio = others.length === 2;
          const secondIdx = hasTrio ? (activeIndex === 0 ? 1 : 0) : null;
          const secondUser = hasTrio ? others[secondIdx] : null;
          const backSrc = secondUser ? getProfilePicUrl(secondUser) : null;

          return (
            <div style={styles.bleedWrap}>
              <div style={styles.stackWrap} key={partner?._id}>
                {/* Back card */}
                <div style={styles.backCard}>
                  {backSrc ? (
                    <img
                      src={backSrc}
                      alt="Other partner"
                      style={styles.backImg}
                      onError={(e) => { e.currentTarget.src = '/default-avatar.png'; }}
                    />
                  ) : (
                    <div style={styles.backPlaceholder} aria-hidden />
                  )}
                </div>

                {/* Front card */}
                <div style={{ ...styles.frontCard, animation: 'fadeProfile 0.3s ease-in-out' }}>
                  <img
                    src={picUrl}
                    alt="Partner"
                    style={styles.frontImg}
                    onError={(e) => { e.currentTarget.src = '/default-avatar.png'; }}
                  />
                  <div style={styles.frontGradient} />

                  {/* Status pill (top-right) — reflects the visible partner */}
                  <div
                    style={{
                      ...styles.statusPill,
                      ...(partnerStatus ? styles.statusPillAccepted : styles.statusPillPending),
                    }}
                  >
                    {partnerStatus ? 'Accepted' : 'Pending'}
                  </div>

                  <div style={styles.frontInfo}>
                    <div style={styles.bigName}>
                      {partner.firstName || 'Someone'}{' '}
                      {partner.lastName ? partner.lastName.charAt(0).toUpperCase() + '.' : ''}
                    </div>
                    <div style={styles.metaRow}>
                      <div style={styles.metaLine}>
                        {gender}{gender && age !== null ? ', ' : ''}{age !== null ? age : ''}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pagination under image */}
              <div style={styles.pagerRow} role="group" aria-label="Match pagination">
                <button
                  type="button"
                  onClick={handlePrev}
                  disabled={!canPrev}
                  aria-disabled={!canPrev}
                  style={{
                    ...styles.pagerBtn,
                    ...(!canPrev ? styles.pagerBtnDisabled : {}),
                  }}
                >
                  <LuChevronLeft style={styles.pagerIcon} aria-hidden />
                </button>

                <span style={styles.pagerText}>
                  {Math.min(activeIndex + 1, pageCount)}/{pageCount}
                </span>

                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!canNext}
                  aria-disabled={!canNext}
                  style={{
                    ...styles.pagerBtn,
                    ...(!canNext ? styles.pagerBtnDisabled : {}),
                  }}
                >
                  <LuChevronRight style={styles.pagerIcon} aria-hidden />
                </button>
              </div>
            </div>
          );
        })()}

        {/* Spacer so fixed action bar doesn't cover content */}
        <div style={{ height: 120 }} />
      </div>

      <style>
        {`
          @keyframes fadeProfile {
            from { opacity: 0; transform: translateY(5px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .spin { animation: spin 1s linear infinite; }
        `}
      </style>

      {/* Fixed bottom action bar */}
      {!hasDeclined && (
        <div style={styles.actionBar}>
          {myStatus === true ? (
            <button style={styles.actionWaiting} disabled>
              <FaSpinner className="spin" style={styles.waitSpinner} />
              Waiting for partners decision...
            </button>
          ) : (
            <>
              <button
                style={{ ...styles.actionSecondary, flex: 1 }}
                onClick={handleDecline}
              >
                Decline
              </button>
              <button
                style={{ ...styles.actionPrimary, flex: 2 }}
                onClick={handleAccept}
              >
                Accept
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

/***************************************************************
 * STYLES
 ***************************************************************/
const styles = {
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  iconCircle: {
    width: 60,
    height: 60,
    borderRadius: '50%',
    backgroundColor: '#f8fafc',
    border: '1px solid #e5e7eb',
    margin: '1.25rem auto 0.75rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
  },

  // content area
  contentArea: {
    flex: 1,
    padding: '0 1rem 2rem',
    textAlign: 'center',
    width: '92%',
    maxWidth: 560,
    margin: '0 auto',
  },

  headingTitle: {
    margin: '4px 0 6px',
    fontSize: '1.6rem',
    fontWeight: 900,
    lineHeight: 1.2,
    letterSpacing: '-0.015em',
    color: '#0F172A',
  },
  subTitle: {
    fontSize: '.95rem',
    color: '#475569',
    fontWeight: 600,
    lineHeight: 1.45,
    margin: '0 0 12px',
  },

  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  pillContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'rgba(255,255,255,0.92)',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '2px 8px',
  },
  pillBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1rem',
    cursor: 'pointer',
    color: '#0F172A',
  },
  pillText: {
    fontSize: '0.85rem',
    fontWeight: 800,
    color: '#0F172A',
    letterSpacing: '.02em',
  },

  spotContainer: {
    position: 'relative',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: '.5rem',
    margin: '0 auto 1.25rem',
    maxWidth: 400,
  },
  imageWrapper: { position: 'relative', width: '100%', height: '100%' },
  spotImage: {
    width: '100%',
    height: 450,
    objectFit: 'cover',
    display: 'block',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  },
  gradientOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '46%',
    borderRadius: 12,
    background:
      'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.18) 60%, rgba(0,0,0,0) 100%)',
  },
  infoOverlay: {
    position: 'absolute',
    left: 16,
    right: 16,
    bottom: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    color: '#fff',
  },
  bigName: {
    fontSize: '2rem',
    fontWeight: 900,
    margin: 0,
    lineHeight: 1.15,
    textAlign: 'left',
    letterSpacing: '-0.015em',
  },
  metaLine: {
    fontSize: '1.1rem',
    fontWeight: 700,
    margin: 0,
    opacity: 0.96,
  },

  acceptedText: {
    marginTop: 8,
    marginBottom: '4rem',
    fontWeight: 600,
    color: '#374151',
  },

  matchOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: '#fff',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '5%',
  },
  overlayContent: { textAlign: 'center', width: '100%' },

  noMatchContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#fff',
    padding: '1rem',
    textAlign: 'center',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  fallbackTitle: {
    fontSize: '1.2rem',
    fontWeight: 700,
    marginBottom: '1rem',
    color: '#101828',
  },
  fallbackBtn: {
    backgroundColor: '#fff',
    color: '#b00',
    padding: '0.8rem 1.5rem',
    border: '1px solid #b00',
    borderRadius: 10,
    fontSize: '1rem',
    cursor: 'pointer',
    fontWeight: 800,
  },

  /* Fixed bottom action bar (33/66 Decline/Accept) */
  actionBar: {
    position: 'fixed',
    inset: 'auto 0 0 0',
    background: '#fff',
    borderTop: '1px solid #eef0f2',
    display: 'flex',
    gap: 10,
    padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
    boxSizing: 'border-box',
    maxWidth: 760,
    margin: '0 auto',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    zIndex: 50,
  },
  actionPrimary: {
    height: 44,
    borderRadius: 12,
    border: '1px solid #0b0b0b',
    background: '#0b0b0b',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'transform .12s ease, box-shadow .12s ease, opacity .12s ease',
  },
  actionPrimaryDisabled: {
    background: '#d1d5db',
    borderColor: '#d1d5db',
    color: '#fff',
    cursor: 'not-allowed',
  },
  actionSecondary: {
    height: 44,
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#0F172A',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'transform .12s ease, box-shadow .12s ease, border-color .12s ease',
    boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
  },
  actionWaiting: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    border: '1px solid #0b0b0b',
    background: '#0b0b0b',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'default',
  },

  waitSpinner: {
    fontSize: 18,
    marginTop: 1,
    marginRight: 8,
    opacity: 0.95,
  },
  pagerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    margin: '6px 0 2px',
  },
  pagerBtn: {
    height: 32,
    width: 40,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#0f172a',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
    cursor: 'pointer',
    transition: 'transform .12s ease, box-shadow .12s ease, opacity .12s ease',
    outline: 'none',
  },
  pagerBtnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  pagerIcon: { fontSize: 16 },
  pagerText: {
    minWidth: 48,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '.5em',
    color: '#0f172a',
  },
  timerWrapper: {
    marginTop: 18,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  timerLabelPill: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    background: '#fff',
    border: '1px solid #e5e7eb',
    fontSize: '.7rem',
    color: '#374151',
    fontWeight: 800,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  digitsRow: { display: 'flex', alignItems: 'baseline', gap: 4 },
  digit: {
    display: 'inline-block',
    fontSize: '2.5rem',
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: '-0.02em',
    color: '#0f172a',
    fontVariantNumeric: 'tabular-nums',
    marginTop: 0,
  },
  colon: {
    fontSize: '2rem',
    fontWeight: 900,
    color: '#9ca3af',
    lineHeight: 1,
    padding: '8px 4px',
    transform: 'translateY(-5px)',
  },

  bleedWrap: {
    position: 'relative',
    width: '100vw',
    left: '50%',
    right: '50%',
    marginLeft: '-50vw',
    marginRight: '-50vw',
  },

  // the actual card width; tweak max to taste (600–640 looks great)
  stackWrap: {
    position: 'relative',
    width: 'min(86vw, 600px)',
    margin: '12px auto 30px',
    height: 465,
  },

  /* Back card (secondary) */
  backCard: {
    position: 'absolute',
    left: 8,
    right: 8,
    bottom: 8,
    height: 440,
    borderRadius: 18,
    background: '#ddd',
    boxShadow: '0 14px 28px rgba(15,23,42,0.14)',
    overflow: 'hidden',
    transform: 'rotate(4.2deg) translateY(6px)',
    transformOrigin: 'center',
  },
  backImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  backPlaceholder: {
    width: '100%',
    height: '100%',
    background: '#ddd',
  },

  /* Front card (primary) */
  frontCard: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: 440,
    borderRadius: 18,
    border: '1px solid #eee',
    background: '#fff',
    boxShadow:
      '0 10px 22px rgba(15,23,42,0.16), 0 2px 0 rgba(15,23,42,0.06) inset',
    overflow: 'hidden',
    zIndex: 2,
  },
  frontImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  frontGradient: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '46%',
    background:
      'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.18) 60%, rgba(0,0,0,0) 100%)',
  },
  frontInfo: {
    position: 'absolute',
    left: 16,
    right: 16,
    bottom: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    color: '#fff',
  },
  headlineWrap: {
    boxSizing: 'border-box',
    maxWidth: 680,
    width: '80%',
    margin: '12px auto 8px',
    padding: '0 12px',
    textAlign: 'center',
    color: '#0b0b0b',
  },
  title: {
    margin: 0,
    fontSize: '1.85rem',
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  highlight: {
    display: 'inline-block',
    backgroundColor: '#3579F6',
    color: '#fff',
    padding: '3px 10px',
    paddingBottom: 4,
    borderRadius: 6,
    transform: 'skewX(-6deg) rotate(-2deg)',
    marginLeft: 0,
  },
  highlightText: {
    display: 'inline-block',
    transform: 'skewX(6deg) rotate(2deg)',
  },
  subheading: {
    marginTop: 6,
    fontSize: '.9rem',
    color: '#6b7280',
    fontWeight: 500,
    lineHeight: 1.45,
  },
  statusPill: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 3,                   // above gradient & text
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: '.72rem',
    fontWeight: 800,
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    lineHeight: 1,
    boxShadow: '0 1px 2px rgba(15,23,42,0.12)',
    pointerEvents: 'none',
  },
  statusPillAccepted: {
    background: '#ecfdf5',
    color: '#065f46',
    border: '1px solid #10b981',
  },
  statusPillPending: {
    background: '#fff7ed',
    color: '#9a3412',
    border: '1px solid #fdba74',
  },
};
